import { FC } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import ChartCard from './ChartCard';
import { useThemeColors } from './useThemeColors';

interface DonutChartCardProps {
  title: string;
  subtitle?: string;
  data: Array<{ name: string; value: number }>;
  height?: number;
}

const DonutChartCard: FC<DonutChartCardProps> = ({ title, subtitle, data, height }) => {
  const colors = useThemeColors();

  if (!data || data.length === 0 || data.every((d) => !d.value)) {
    return (
      <ChartCard title={title} subtitle={subtitle} height={height}>
        <div className="w-full h-full flex items-center justify-center text-content-muted text-sm">
          Sem dados no período
        </div>
      </ChartCard>
    );
  }

  const total = data.reduce((acc, d) => acc + d.value, 0);

  return (
    <ChartCard title={title} subtitle={subtitle} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="55%"
            outerRadius="85%"
            stroke={colors.surface}
            strokeWidth={2}
          >
            {data.map((_, idx) => (
              <Cell key={idx} fill={colors.palette[idx % colors.palette.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              color: colors.text,
              fontSize: 14,
            }}
            formatter={(value, name) => {
              const num = typeof value === 'number' ? value : Number(value ?? 0);
              const pct = total > 0 ? ((num / total) * 100).toFixed(1) : '0';
              return [`${num} (${pct}%)`, String(name ?? '')];
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-content-muted">
        {data.map((d, idx) => (
          <span key={d.name} className="inline-flex items-center gap-1">
            <span
              className="inline-block w-2 h-2 rounded-sm"
              style={{ backgroundColor: colors.palette[idx % colors.palette.length] }}
            />
            {d.name}
          </span>
        ))}
      </div>
    </ChartCard>
  );
};

export default DonutChartCard;
